/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, Ref, useState } from 'react';
import './styles/Popover.scss';

import { ClickAwayListener, Popper as MuiPopper, Stack, styled } from '@mui/material';

import { classNames, componentClassName } from '@dw/utils';

import { IModalPropsBase, Modal } from './Modal';
import { useScreenSize } from '@dw/hooks';

/**
 * Overlays MUI renders in a portal at the document root (menus, date pickers, autocompletes,
 * dialogs). A click inside one of these belongs to the popover's own content even though it sits
 * outside the popover's DOM subtree.
 */
const PORTALLED_OVERLAY_SELECTOR = [
  '.MuiModal-root',
  '.MuiPopover-root',
  '.MuiMenu-root',
  '.MuiPickersPopper-root',
  '.MuiAutocomplete-popper',
  '.MuiTooltip-popper',
].join(', ');

export interface IPopoverProps extends IModalPropsBase {
  anchorEl?: Element;
  maxWidth?: string;
  minWidth?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  noPadding?: boolean;
  children: React.ReactNode;
}
export const Popover: FunctionComponent<IPopoverProps> = (props: IPopoverProps) => {

  const {
    anchorEl,
    maxWidth,
    minWidth,
    children,
    placement,
    noPadding,
    ...modalBaseProps
  } = props;

  const { isMobile } = useScreenSize();

  const [arrowRef, setArrowRef] = useState<Ref<HTMLDivElement>>(null);

  const cn = componentClassName('popover');

  if (!anchorEl) {
    return null;
  } else if (isMobile) {
    return (
      <Modal
        {...modalBaseProps}
      >
        {children}
      </Modal>
    )
  }

  return (
    <ClickAwayListener
      // Listen on the PRESS, not the click. MUI's Select opens its menu on mousedown and mounts a
      // full-screen backdrop; by mouseup the press and release targets differ, so the browser
      // retargets the click event to their common ancestor (<body>) - which defeats any
      // element-based guard and closed the popover the moment a select inside it was opened.
      // Press events always land on the real element under the pointer, before overlays mount.
      mouseEvent='onMouseDown'
      touchEvent='onTouchStart'
      onClickAway={(event) => {
        const target = event.target;
        if (target instanceof Element) {
          // Presses on the anchor are the anchor's own business - its onClick toggles the popover.
          // Handling them here too would race the toggle (close on press, reopen on click).
          if (anchorEl.contains(target)) {
            return;
          }
          // Select menus, date pickers, autocompletes and nested dialogs render through a portal at
          // the document root, so they are NOT inside this listener's subtree. Only ignore overlays
          // spawned ABOVE this popover (they never contain our anchor); a Modal that HOSTS the
          // popover contains the anchor and must still close it, otherwise a popover anchored
          // inside a dialog could never be dismissed by clicking around it.
          const overlay = target.closest(PORTALLED_OVERLAY_SELECTOR);
          if (overlay && !overlay.contains(anchorEl)) {
            return;
          }
        }
        modalBaseProps.onClose();
      }}>
      <MuiPopper
        open={modalBaseProps.open}
        placement={placement ?? 'right'}
        anchorEl={anchorEl}
        modifiers={[
          {
            name: 'flip',
            options: {
                altBoundary: false,
                fallbackPlacements: ['right', 'bottom', 'left', 'top'], // This is to prevent popper from being cut off at bottom or top, and just giving it the option to try to the left and right of the input as well
            }
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          }
        ]}
        className={cn()}
      >
        <Stack maxWidth={maxWidth} minWidth={minWidth} className={classNames(cn('content'), {
          [cn('content', 'no-padding')]: noPadding
        })}>
          <Arrow ref={setArrowRef as any} className={cn('arrow')} />

          {children}
        </Stack>
      </MuiPopper>
    </ClickAwayListener>
  );
}

const Arrow = styled('div')({
  position: 'absolute',
  fontSize: 7,
  width: '3em',
  height: '3em',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  },
});